import { Button as ButtonUI } from '@/global/components/ui/button'
import { useLocationTestId } from '@/hooks/useTestId'
import { cn } from '@/lib/utils'
import { Button } from 'global/components/Button'
import AjudaFiltros from 'global/guides/ContentGuide/AjudaFiltros'
import localeText from 'locales/datagrid'
import {
    ExternalLinkIcon,
    HelpCircleIcon,
    PlusIcon,
    Trash2Icon,
    XIcon,
} from 'lucide-react'
import React from 'react'
import { toast } from 'sonner'
import HorizontalDivider from '../../Divider/HorizontalDivider'
import { Label } from '../../ui/label'
import { filtroVazio } from '../hooks/useDefaultFilterModel'
import useFilterPresets from '../hooks/useFilterPresets'
import FilterModelPro, {
    GridFilterItemPro,
    GridFilterModelPro,
} from '../utils/filterModelPro'
import { useTableApiContext } from '../utils/table-context'
import { GridColDefPro } from '../utils/types'
import FieldColuna from './FieldColuna'
import FieldLinkOperator from './FieldLinkOperator'
import FieldOperator from './FieldOperator'
import FieldValue from './FieldValue'
import FilterPreset from './FilterPreset'
import SalvarFiltro from './SalvarFiltro'

export default function FilterPanelPro() {
    const {
        filterModelPro: filterModel,
        onFilterModelChangePro: onFilterModelChange,
        columns: apiColumns,
        filtroPadrao,
        setFiltroVisivel,
    } = useTableApiContext()
    const adicionarFiltroTestId = useLocationTestId('filtros-adicionar')

    const columns = apiColumns.filter(c => c.filterable !== false)
    const presets = useFilterPresets({ columns })
    const totalFiltros = filterModel.items.length

    function gerarLinkFiltro() {
        const link = window.location.href.split('?')[0] + '?grid-filter='
        navigator.clipboard.writeText(
            link + new FilterModelPro(filterModel).toUrlParams(),
        )
        toast.success('Link copiado para a área de transferência')
    }

    function adicionarFiltro() {
        onFilterModelChange(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    columnField: columns[0].field,
                    operatorValue: FilterModelPro.getDefaultOperator(
                        columns[0].type,
                    ),
                    value: '',
                    id: FilterModelPro.gerarID(prev.items),
                },
            ],
        }))
    }

    return (
        <div className="flex flex-col w-full border-1 border-b-0">
            <header className="flex items-center justify-between gap-2 px-4 pt-1 max-sm:justify-end">
                <CabecalhoLogica items={filterModel.items} />
                <div className="flex items-center gap-2">
                    <AjudaFiltros
                        Icon={
                            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                <HelpCircleIcon className="size-4" /> Como
                                funcionam os filtros
                            </span>
                        }
                    />
                    <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={() => setFiltroVisivel(false)}
                    >
                        <XIcon className="size-4! -mt-0.5" />
                        Fechar
                    </Button>
                </div>
            </header>
            <section className="px-4 pt-1 pb-2">
                {!totalFiltros ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            size="small"
                            startIcon={<PlusIcon />}
                            data-testid={adicionarFiltroTestId}
                            onClick={adicionarFiltro}
                        >
                            Adicionar filtro
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            ou escolha um filtro salvo abaixo
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2 max-h-44 overflow-y-auto scrollbar-gutter-stable">
                        {filterModel.items.map((item, index) => (
                            <FilterOption
                                key={item.id}
                                item={item}
                                onFilterModelChange={onFilterModelChange}
                                columns={columns}
                                isFirstItem={index === 0}
                            />
                        ))}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PlusIcon />}
                            data-testid={adicionarFiltroTestId}
                            onClick={adicionarFiltro}
                            className="w-fit md:ml-[calc(6rem+0.7rem)]"
                        >
                            Adicionar filtro
                        </Button>
                    </div>
                )}
            </section>
            <HorizontalDivider className="my-1 py-0 px-4 h-px" />
            <footer className="flex flex-wrap items-center justify-between gap-2 px-4 pb-1">
                <Button
                    variant="text"
                    color="error"
                    size="small"
                    startIcon={<Trash2Icon />}
                    disabled={!totalFiltros}
                    onClick={() =>
                        onFilterModelChange(prev => ({
                            ...prev,
                            items: filtroVazio.items,
                        }))
                    }
                >
                    Limpar todos os filtros
                    {!!totalFiltros && ` (${totalFiltros})`}
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ExternalLinkIcon />}
                        title="Clique para copiar o link do filtro"
                        disabled={!totalFiltros}
                        onClick={gerarLinkFiltro}
                    >
                        Compartilhar
                    </Button>
                    <FilterPreset
                        onFilterModelChange={onFilterModelChange}
                        presets={presets}
                        filtroPadrao={filtroPadrao}
                    />
                    <SalvarFiltro
                        disabled={!totalFiltros}
                        onSalvar={nome =>
                            presets.adicionarPreset(filterModel.items, nome)
                        }
                    />
                </div>
            </footer>
        </div>
    )
}

function CabecalhoLogica({ items }: { items: GridFilterItemPro[] }) {
    const classe =
        'text-xs uppercase tracking-wide text-muted-foreground max-sm:hidden'

    if (!items.length)
        return (
            <Label className={classe}>
                Nenhum filtro aplicado, todas as linhas estão visíveis
            </Label>
        )

    return (
        <Label className={classe}>Mostrar linhas que atendem aos filtros</Label>
    )
}

/**
 * Campos da linha com 40px em vez dos 48px do `size="small"`. Rótulo (17px) e
 * valor (23px) ainda cabem sem padding vertical. Valor em 14px, não 16px.
 */
const CAMPO_COMPACTO = cn(
    '[&_:is([data-slot=input],[data-slot=select-trigger],[data-slot=combobox-trigger],button.h-12)]:h-10',
    '[&_:is([data-slot=input],[data-slot=select-trigger],[data-slot=combobox-trigger],button.h-12)]:py-0',
    '[&_.text-base]:text-sm',
)

const FilterOption = ({
    item,
    onFilterModelChange,
    columns,
    isFirstItem,
}: {
    item: GridFilterItemPro
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
    columns: GridColDefPro[]
    isFirstItem: boolean
}) => {
    const column = columns.find(column => column.field === item.columnField)

    const MemoizedLinkOperator = React.useMemo(
        () => (
            <FieldLinkOperator
                item={item}
                onFilterModelChange={onFilterModelChange}
            />
        ),
        [item.id, item.linkOperator],
    )

    const MemoizedFieldOperator = React.useMemo(
        () =>
            column ? (
                <FieldOperator
                    item={item}
                    column={column}
                    onFilterModelChange={onFilterModelChange}
                    className="w-full"
                />
            ) : null,
        [item.id, item.operatorValue, item.columnField],
    )

    const MemoizedFieldColuna = React.useMemo(
        () => (
            <FieldColuna
                item={item}
                columns={columns}
                onFilterModelChange={onFilterModelChange}
                className="w-full"
            />
        ),
        [item.id, item.columnField],
    )

    if (!column) return null

    return (
        <div
            className={cn(
                'grid gap-2 items-center',
                'grid-cols-[6rem_1fr_4rem]',
                'md:grid-cols-[6rem_minmax(8rem,1fr)_minmax(8rem,1fr)_minmax(8rem,1fr)_4rem]',
                '*:p-0.5 max-md:border max-md:rounded-md max-md:p-1',
                CAMPO_COMPACTO,
            )}
        >
            <div className="flex justify-center">
                {isFirstItem ? (
                    <Label className="text-sm uppercase text-muted-foreground">
                        Onde
                    </Label>
                ) : (
                    MemoizedLinkOperator
                )}
            </div>
            <div className="col-span-full row-start-2 md:col-span-1 md:row-start-auto">
                {MemoizedFieldColuna}
            </div>
            <div className="col-span-full md:col-span-1">
                {MemoizedFieldOperator}
            </div>
            <div className="col-span-full md:col-span-1">
                <FieldValue
                    item={item}
                    column={column}
                    onFilterModelChange={onFilterModelChange}
                />
            </div>
            <ButtonUI
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                    onFilterModelChange(prev => ({
                        ...prev,
                        items: prev.items.filter(({ id }) => id !== item.id),
                    }))
                }
                title={localeText.filterPanelDeleteIconLabel}
                className="col-start-3 row-start-1 md:col-start-auto md:row-start-auto size-10 rounded-full"
            >
                <Trash2Icon className="text-error" />
            </ButtonUI>
        </div>
    )
}
